import React, { useEffect, useMemo, useState } from 'react';

import './AlertFilters.scss';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { SalvButton, SalvCheckbox, Size } from '@salvapp/ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { faPlay, faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';
import {
  ApiAlertType,
  ApiAttributeMapping,
  ApiFilteredAlert,
  ApiSortDirection,
  FindAlertFilterResultsOrderByEnum,
} from '../../api';
import { AppState } from '../../store/store';
import { useDispatch } from 'react-redux';
import { isNavigableCellClicked, pluralize } from '../../common/helper';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import UnknownSizeLoadMoreTable from '../../table/UnknownSizeLoadMoreTable';
import { Spinner } from '../../feedback/Spinner';
import {
  assignAndGoToNextAlertInFilter,
  assignBulk,
  assignFilteredAlert,
  exportBulk,
  fetchFilteredAlerts,
  fetchFilteredAlertsNextBatch,
  unassignFilteredAlert,
} from './filteredAlertsActions';
import AlertedEntityDetails from './AlertedEntityDetails';
import {
  AssignNextAlertFromFilterOrderByEnum,
  InternalApiAlertFilter,
  InternalApiAlertId,
  InternalApiAlertType,
  InternalApiSortDirection,
  InternalApiUserBasicDetails,
} from '../../api-internal';
import { fetchAlertFilters } from './alertFiltersActions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../../feedback/feedbackReducer';
import { AlertFilterResultsExportModal, Template } from './AlertFilterResultsExportModal';
import { useMe } from '../../organisation/useMe';
import InvestigationAssignmentDropdown from '../InvestigationAssignmentDropdown';
import { CAN_MODIFY_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER, CAN_MODIFY_SANCTION_ALERT } from '../../auth/permissions';
import DisabledField from '../../custom-lists/DisabledField';
import { FilterResultsAlertDetailsCell } from './helper';

export const BATCH_SIZE = 50;
export const MAX_EXPORT_SIZE = 200000;

interface AlertFilterResultsProps {
  filter: InternalApiAlertFilter;
  mappings: { [p: string]: ApiAttributeMapping };
  searchParams: URLSearchParams;
}

const getNextOffsetCreatedTime = (
  alerts: Array<ApiFilteredAlert>,
  sortBy: FindAlertFilterResultsOrderByEnum,
  sortDirection: InternalApiSortDirection
): Date => {
  let timeValues: any[];

  if (sortBy === FindAlertFilterResultsOrderByEnum.CREATED_TIME) {
    timeValues = alerts.map((it) => it.createdTime!.getTime());
  } else {
    timeValues = alerts.map((it) => it.statusUpdatedTime!.getTime());
  }

  if (sortDirection === InternalApiSortDirection.DESC) {
    return new Date(Math.min(...timeValues));
  }
  return new Date(Math.max(...timeValues));
};

const getLastAlert = (alerts: Array<ApiFilteredAlert>): [number, ApiAlertType] =>
  toAlertIdAndType(alerts[alerts.length - 1]);

const toAlertIdAndType = (alert: ApiFilteredAlert): [number, ApiAlertType] => {
  if (alert.screeningAlertId) {
    return [alert.screeningAlertId, ApiAlertType.SCREENING];
  } else if (alert.monitoringAlertId) {
    return [alert.monitoringAlertId, ApiAlertType.MONITORING];
  } else if (alert.bridgeAlertId) {
    return [alert.bridgeAlertId, ApiAlertType.BRIDGE];
  } else if (alert.manualAlertId) {
    return [alert.manualAlertId, ApiAlertType.MANUAL];
  } else throw new Error(); // Unreachable
};

export const toAlertId = (alert: ApiFilteredAlert) => {
  const [id, type] = toAlertIdAndType(alert);
  return {
    alertId: id,
    alertType: InternalApiAlertType[type as keyof typeof InternalApiAlertType],
  };
};

const indexOf = (id: InternalApiAlertId, list: InternalApiAlertId[]) =>
  list.findIndex((alert) => alert.alertId == id.alertId && alert.alertType == id.alertType);

// This is a temporary function until we migrate public endpoints to internal
const getSortDirection = (sortDirection: InternalApiSortDirection): ApiSortDirection =>
  ApiSortDirection[sortDirection] || ApiSortDirection.DESC;

function getSortDirectionIcon(
  sortDirection: InternalApiSortDirection,
  sortBy: FindAlertFilterResultsOrderByEnum,
  currentSortBy: FindAlertFilterResultsOrderByEnum
) {
  if (sortBy !== currentSortBy) {
    return <FontAwesomeIcon icon={faSort} />;
  } else {
    if (sortDirection === InternalApiSortDirection.DESC) {
      return <FontAwesomeIcon icon={faSortDown} aria-label='descending' />;
    } else {
      return <FontAwesomeIcon icon={faSortUp} aria-label='ascending' />;
    }
  }
}

export const AlertFilterResults = ({ filter, mappings, searchParams }: AlertFilterResultsProps): React.JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { features, currentOrganisation } = useMe();
  const [offsetCreatedTime, setOffsetCreatedTime] = useState<Date>();
  const [currentAlertId, setCurrentAlertId] = useState<number>();
  const [currentAlertType, setCurrentAlertType] = useState<ApiAlertType>();
  const [selectedAlerts, setSelectedAlerts] = useState(Array<InternalApiAlertId>());
  const [previousRow, setPreviousRow] = useState<number | null>(null);
  const [wholeFilterSelected, setWholeFilterSelected] = useState<boolean>(false);

  const sortDirection: InternalApiSortDirection =
    searchParams.get('direction') === InternalApiSortDirection.ASC
      ? InternalApiSortDirection.ASC
      : InternalApiSortDirection.DESC;
  const sortBy: FindAlertFilterResultsOrderByEnum =
    searchParams.get('sortBy') === FindAlertFilterResultsOrderByEnum.STATUS_UPDATED_TIME
      ? FindAlertFilterResultsOrderByEnum.STATUS_UPDATED_TIME
      : FindAlertFilterResultsOrderByEnum.CREATED_TIME;
  const assignSortBy: AssignNextAlertFromFilterOrderByEnum =
    searchParams.get('sortBy') === FindAlertFilterResultsOrderByEnum.STATUS_UPDATED_TIME
      ? AssignNextAlertFromFilterOrderByEnum.STATUS_UPDATED_TIME
      : AssignNextAlertFromFilterOrderByEnum.CREATED_TIME;
  const screeningEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features);
  const monitoringEnabled = isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features);
  const navigateToAlert = isFeatureEnabled(ORGANISATION_FEATURE.MONITORING_ALERT_PAGE, features);
  const reloadFiltersAndResults = () => {
    dispatch(fetchAlertFilters());
    dispatch(fetchFilteredAlerts(filter.id, sortBy, getSortDirection(sortDirection), BATCH_SIZE));
  };

  const alerts = useSelector((state: AppState) => state.filteredAlerts.alerts);
  const isLoading = useSelector((state: AppState) => state.filteredAlerts.isLoading);

  const allStatuses = useSelector((state: AppState) => state.screeningAlertStatuses.statuses, shallowEqual);

  const flipSortDirection = (sortBy: FindAlertFilterResultsOrderByEnum) => {
    if (sortDirection === InternalApiSortDirection.DESC) {
      navigate({
        search: `?id=${filter?.id}&direction=ASC&sortBy=${sortBy}`,
      });
    } else {
      navigate({
        search: `?id=${filter?.id}&direction=DESC&sortBy=${sortBy}`,
      });
    }
  };

  const navigateTo = (alert: ApiFilteredAlert, openInNewWindow = false): void => {
    const location = (() => {
      if (alert.monitoringAlertId && !navigateToAlert) {
        return `/customer/${alert.alertedEntity?.person?.id}`;
      } else if (alert.monitoringAlertId) {
        return `/monitoring/alerts/${alert.monitoringAlertId}`;
      } else if (alert.screeningAlertId) {
        return `/screening/alerts/${alert.screeningAlertId}`;
      } else if (alert.bridgeAlertId) {
        return `/bridge/alerts/${alert.bridgeAlertId}`;
      } else if (alert.manualAlertId) {
        return `/manual/alerts/${alert.manualAlertId}`;
      } else {
        throw new Error('Unknown alert type');
      }
    })();

    if (openInNewWindow) {
      window.open(location, '_blank');
    } else {
      navigate(location);
    }
  };

  useEffect(() => {
    if (alerts.length > 0) {
      setOffsetCreatedTime(getNextOffsetCreatedTime(alerts, sortBy, sortDirection));
      const [id, type] = getLastAlert(alerts);
      setCurrentAlertId(id);
      setCurrentAlertType(type);
    }
  }, [alerts]);

  useEffect(() => {
    dispatch(fetchFilteredAlerts(filter.id, sortBy, getSortDirection(sortDirection), BATCH_SIZE));
    if (selectedAlerts.length > 0) {
      selectAllAlertsOnPage();
    }
  }, [filter, sortDirection]);

  const loadMore = () => {
    return fetchFilteredAlertsNextBatch(
      filter.id,
      sortBy,
      getSortDirection(sortDirection),
      BATCH_SIZE,
      offsetCreatedTime!,
      currentAlertId!,
      currentAlertType!
    )(dispatch);
  };

  const assignAlert = (alert: ApiFilteredAlert, user: InternalApiUserBasicDetails) =>
    dispatch(
      assignFilteredAlert(alert, {
        id: user.id!,
        name: user.name,
        email: user.email,
      })
    );

  const unassignAlert = (alert: ApiFilteredAlert) => dispatch(unassignFilteredAlert(alert));

  const selectAlert = (id: InternalApiAlertId, index: number, selectMultiple = true) => {
    const newSelectedAlerts = [...selectedAlerts];
    const indexOfAlertId = indexOf(id, newSelectedAlerts);

    if (indexOfAlertId === -1) {
      // if shift key is being pressed upon click and there is a row that was previously selected, grab all rows in between including both previous and current clicked rows
      if (selectMultiple && typeof previousRow === 'number' && previousRow > -1) {
        const start = previousRow > index ? index : previousRow;
        const end = previousRow > index ? previousRow : index;
        for (let i = start; i <= end; i++) {
          const alertId = toAlertId(alerts[i]);
          if (indexOf(alertId, newSelectedAlerts) === -1) {
            newSelectedAlerts.push(alertId);
          }
        }
      } else {
        // add alert to selected alerts
        newSelectedAlerts.push(id);
      }
      setPreviousRow(index);
    } else {
      // remove alert from selected alerts
      newSelectedAlerts.splice(indexOfAlertId, 1);
      setPreviousRow(null);
    }
    setSelectedAlerts(newSelectedAlerts);
    updateWholeFilterSelected(newSelectedAlerts);
  };

  const selectAllAlertsOnPage = () => {
    const newSelectedAlerts = selectedAlerts.length > 0 ? [] : alerts.map((a) => toAlertId(a));
    setSelectedAlerts(newSelectedAlerts);
    updateWholeFilterSelected(newSelectedAlerts);
  };

  const updateWholeFilterSelected = (newSelectedAlerts: InternalApiAlertId[], selectAll: boolean = false) => {
    setWholeFilterSelected(selectAll || filter.alertCount! === newSelectedAlerts.length);
  };

  const isAlertSelected = (id: InternalApiAlertId) => indexOf(id, selectedAlerts) !== -1;

  const getAlertsCount = () => {
    return pluralize(wholeFilterSelected ? filter.alertCount! : selectedAlerts.length, 'alert', 'alerts', true);
  };

  const assignAlertsBulk = async (assignee?: InternalApiUserBasicDetails) => {
    dispatch(
      showFeedback({
        message: assignee
          ? `Assigning ${getAlertsCount()} to ${assignee.name}. This might take few seconds...`
          : `Unassigning ${getAlertsCount()}. This might take few seconds...`,
        variant: 'info',
      })
    );
    dispatch(assignBulk(filter.id, assignee?.id, wholeFilterSelected ? undefined : selectedAlerts));
    dispatch(
      showFeedback({
        message: assignee
          ? `Successfully assigned ${getAlertsCount()} to ${assignee.name}`
          : `Successfully unassigned ${getAlertsCount()}`,
        variant: 'success',
      })
    );
    setSelectedAlerts([]);
    updateWholeFilterSelected([]);
    reloadFiltersAndResults();
  };

  const exportAlertsBulk = async (template: Template) => {
    dispatch(exportBulk(filter.id, template, wholeFilterSelected ? undefined : selectedAlerts));
  };

  const assignAlertsWithConfirmation = (assignee?: InternalApiUserBasicDetails) => {
    dispatch(
      showConfirmationModal({
        title: assignee
          ? `Are you sure you want to assign ${getAlertsCount()} to ${assignee.name}?`
          : `Are you sure you want to unassign ${getAlertsCount()}?`,
        onConfirm() {
          assignAlertsBulk(assignee);
        },
        confirmationText: 'Yes',
        cancelText: 'No, go back',
      })
    );
  };

  const tableName = () => {
    if (selectedAlerts.length === 0) {
      return filter.name;
    } else if (selectedAlerts.length === alerts.length && wholeFilterSelected) {
      return `All ${getAlertsCount()} selected`;
    } else if (selectedAlerts.length === alerts.length) {
      return `${getAlertsCount()} on this view selected`;
    } else {
      return `${getAlertsCount()} selected`;
    }
  };

  const header = (
    <tr>
      <th className='alert-checkbox'>
        <SalvCheckbox
          data-testid='select-all-checkbox'
          label=''
          ariaLabel='select-all-checkbox'
          selected={selectedAlerts.length > 0}
          indeterminate={alerts.length !== selectedAlerts.length}
          onChange={() => selectAllAlertsOnPage()}
        />
      </th>
      {(monitoringEnabled || screeningEnabled) && <th>ENTITY</th>}
      <th onClick={() => flipSortDirection(FindAlertFilterResultsOrderByEnum.CREATED_TIME)} className='cursor-pointer'>
        {getSortDirectionIcon(sortDirection, FindAlertFilterResultsOrderByEnum.CREATED_TIME, sortBy)}
        <span className='m-2'>CREATED TIME</span>
      </th>
      <th>REASON</th>
      <th>STATUS</th>
      <th
        onClick={() => flipSortDirection(FindAlertFilterResultsOrderByEnum.STATUS_UPDATED_TIME)}
        className='cursor-pointer'
      >
        {getSortDirectionIcon(sortDirection, FindAlertFilterResultsOrderByEnum.STATUS_UPDATED_TIME, sortBy)}
        <span className='m-2'>STATUS LAST UPDATED</span>
      </th>
      <th>ASSIGNEE</th>
    </tr>
  );

  const getAlertId = (alert: ApiFilteredAlert): string => {
    if (alert.screeningAlertId) return `s-${alert.screeningAlertId}`;
    else if (alert.monitoringAlertId) return `m-${alert.monitoringAlertId}`;
    else if (alert.bridgeAlertId) return `b-${alert.bridgeAlertId}`;
    else if (alert.manualAlertId) return `ma-${alert.manualAlertId}`;
    else return '';
  };

  const tableRows = useMemo(
    () =>
      alerts.map((alert, index) => (
        <tr
          key={`${getAlertId(alert)}-${alert.statusId}-${alert.assignee?.id}`}
          className={isAlertSelected(toAlertId(alert)) ? 'selected-row' : 'clickable-row'}
          onClick={(event: any) => isNavigableCellClicked(event) && navigateTo(alert, event.metaKey || event.ctrlKey)}
        >
          {
            <td
              data-testid={'alert-checkbox-' + index}
              onClick={(event) => {
                event.stopPropagation();
                selectAlert(toAlertId(alert), index, event.shiftKey);
              }}
              className='alert-checkbox'
            >
              <SalvCheckbox
                label=''
                ariaLabel={`alert-checkbox-${index}`}
                selected={isAlertSelected(toAlertId(alert))}
              />
            </td>
          }
          {(monitoringEnabled || screeningEnabled) && (
            <td data-testid='alerted-entity-details'>
              {alert.alertedEntity ? (
                <AlertedEntityDetails
                  entity={alert.alertedEntity}
                  mappings={mappings}
                  organisationCurrency={currentOrganisation?.currency}
                />
              ) : (
                <span className='unidentified'>No entities linked</span>
              )}
            </td>
          )}
          <td>
            <FormattedDateTime date={alert.createdTime} showTime />
          </td>
          <td>
            <FilterResultsAlertDetailsCell alert={alert} />
          </td>
          <td>{allStatuses.find((status) => status.id === alert.statusId)?.textName}</td>
          <td>
            <FormattedDateTime date={alert.statusUpdatedTime} showTime />
          </td>
          <td>
            <InvestigationAssignmentDropdown
              size={Size.SMALL}
              assignee={alert.assignee}
              onChange={(user: InternalApiUserBasicDetails) => assignAlert(alert, user)}
              onUnassign={() => unassignAlert(alert)}
              anyPermissions={[CAN_MODIFY_ALERT, CAN_MODIFY_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}
            />
          </td>
        </tr>
      )),
    [alerts, currentOrganisation, mappings, selectedAlerts, wholeFilterSelected, navigateTo]
  );

  const exportDisabled = () => {
    return (wholeFilterSelected && filter.alertCount! > MAX_EXPORT_SIZE) || selectedAlerts.length > MAX_EXPORT_SIZE;
  };

  return (
    <div className='alert-filters-results'>
      <UnknownSizeLoadMoreTable
        title={tableName()}
        header={header}
        rows={tableRows}
        fetchData={loadMore}
        maxBatchSize={BATCH_SIZE}
        loading={isLoading}
        className='alert-filters-load-more-table'
        cardHeaderLeftPane={
          <>
            {selectedAlerts.length > 0 && selectedAlerts.length === alerts.length && (
              <SalvButton
                data-testid='select-all-in-filter-button'
                variant='primary'
                className='select-all-in-filter-button'
                size={Size.SMALL}
                onClick={() =>
                  wholeFilterSelected ? selectAllAlertsOnPage() : updateWholeFilterSelected(selectedAlerts, true)
                }
                label={wholeFilterSelected ? 'Clear selection' : `Select all ${filter.alertCount} alerts`}
              />
            )}
            {selectedAlerts.length > 0 && (
              <span data-testid='bulk-assign-dropdown' className='bulk-assign-dropdown'>
                <InvestigationAssignmentDropdown
                  size={Size.SMALL}
                  onChange={(user: InternalApiUserBasicDetails) => assignAlertsWithConfirmation(user)}
                  onUnassign={() => assignAlertsWithConfirmation()}
                  dropdownAlign='start'
                  anyPermissions={[CAN_MODIFY_ALERT, CAN_MODIFY_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}
                />
              </span>
            )}
            {selectedAlerts.length > 0 && (
              <DisabledField
                isEditingDisabled={exportDisabled()}
                tooltipText={`Exporting more than ${MAX_EXPORT_SIZE} alerts is not allowed`}
              >
                <AlertFilterResultsExportModal
                  onExport={(template) => {
                    exportAlertsBulk(template);
                  }}
                  disabled={exportDisabled()}
                />
              </DisabledField>
            )}
          </>
        }
        cardHeaderRightPane={
          selectedAlerts.length == 0 && (
            <>
              {!filter.inSync && (
                <>
                  <Spinner loading={true} small={true} />
                  <span className='loading-more m-3'>Table loading. Refresh to see the latest results</span>
                </>
              )}
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                className='me-2 p-3'
                icon={faArrowsRotate}
                ariaLabel='Refresh'
                onClick={reloadFiltersAndResults}
              />
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                className='p-3'
                icon={faPlay}
                ariaLabel='Assign alert to yourself for solving'
                onClick={() => {
                  dispatch(assignAndGoToNextAlertInFilter(filter.id, sortDirection, assignSortBy, navigate));
                }}
              />
            </>
          )
        }
      />
    </div>
  );
};

export default AlertFilterResults;
